// §4.3 — MARGIN GUARDS, /sales/margin-guards (Module 09, slice P3-A part 2).
// The §2.11 editor: the minimum margin a price may carry before §3.2 flags the
// line as breached and the quote has to be approved.
//
// THE READ RIDES `sales.tier.manage`, NOT `sales.read` (§5's own note: a
// guard's `min_margin_pct` IS margin intelligence). So this screen is offered
// only to holders of that code and the query is `enabled` on the same fact — a
// read we know answers 403 is the dead call D-088 forbids, and its cost is a
// red card with a Retry that can never succeed.
//
// TWO OPTIONS THE API ALWAYS REFUSES ARE NOT OFFERED (P3-UX, D-425):
//   * `scope='family'` — module 01's shipped schema has NO product_families
//     table, so the API answers 422 FAMILY_SCOPE_UNAVAILABLE for every family
//     guard. The option is absent from the scope picker: an option whose
//     every submission is refused is a control that exists to fail.
//   * `cost_basis='moving_avg'` — a legal column value the API refuses to
//     WRITE until module 11's cost ledger (422 COST_BASIS_UNAVAILABLE). The
//     form offered it with a hint until the copy budget; it now saves the
//     standard basis and draws no control for the other. A guard that holds
//     the moving-average basis still shows it by name in the list.
//
// WHO MAY WRITE. §7's guard-edit cell is group_admin ALONE and the service
// enforces it; the mirror cannot distinguish ga from fa on the shared
// `sales.tier.manage` code, so the controls are offered to tier.manage holders
// with the note — the same call the off-cut class editor makes, for the same
// reason.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useProductIndex, useProductSearch } from "../../production/hooks/production-queries";
import { MutationFailureNote } from "../mutation-failure-note";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import {
  useCreateMarginGuard,
  useMarginGuards,
  useUpdateMarginGuard,
  type MarginGuard,
  type MarginGuardCreateRequest,
} from "../hooks/sales-queries";
import { bookClientPager } from "../hooks/book-pages";

/** The scopes this screen OFFERS. `family` is deliberately not among them —
 * see the header: every family submission is refused 422 today. */
const OFFERED_SCOPES = ["default", "product"] as const;
type OfferedScope = (typeof OFFERED_SCOPES)[number];

const PCT = /^(100(\.00?)?|\d{1,2}(\.\d{1,2})?)$/u;

export function SalesMarginGuardsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();

  const canManage = perms.has("sales.tier.manage");
  const refused = isSalesReadRefused(perms, canManage);
  const guards = useMarginGuards(!refused && canManage);

  const create = useCreateMarginGuard();
  const update = useUpdateMarginGuard();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (guards.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [guards.error, navigate]);

  const rows = useMemo(() => guards.data ?? [], [guards.data]);
  // Product names for the `product` scope rows — one read per distinct id,
  // cached; an unresolved id renders as a short-id chip, never a broken name.
  const products = useProductIndex(
    useMemo(
      () => rows.map((row) => row.product_id).filter((id): id is string => id !== null),
      [rows],
    ),
  );

  const columns: DataTableColumn<MarginGuard>[] = [
    {
      key: "scope",
      header: t("sales.guards.colScope"),
      render: (row) => t(`sales.guards.scope.${row.scope}`),
    },
    {
      key: "product_id",
      header: t("sales.guards.colTarget"),
      render: (row) =>
        row.scope === "default" ? (
          t("sales.guards.tenantDefault")
        ) : row.scope === "family" ? (
          // A row the API can still SERVE (created before the constraint was
          // known, or by a later slice) — named honestly rather than blank.
          <RefLabel value={t("sales.guards.familyTarget")} id={row.family_id} />
        ) : (
          <RefLabel
            value={products.byId.get(row.product_id ?? "")?.sku}
            id={row.product_id}
            pending={products.isPending}
          />
        ),
    },
    {
      key: "min_margin_pct",
      header: t("sales.guards.colMinMargin"),
      numeric: true,
      render: (row) => t("sales.guards.pct", { pct: row.min_margin_pct }),
    },
    {
      key: "cost_basis",
      header: t("sales.guards.colCostBasis"),
      render: (row) => t(`sales.guards.basis.${row.cost_basis}`),
    },
    {
      key: "status",
      header: t("sales.guards.colStatus"),
      render: (row) => (
        <StatusChip variant={row.status === "active" ? "ok" : "neutral"}>
          {row.status === "active"
            ? t("sales.customers.statusActive")
            : t("sales.customers.statusArchived")}
        </StatusChip>
      ),
    },
    {
      key: "actions",
      header: t("sales.guards.colActions"),
      actions: true,
      render: (row) => (
        <Button
          size="sm"
          data-testid="sales-margin-guard-toggle"
          busy={update.isPending}
          busyLabel={t("sales.common.saving")}
          onClick={() => {
            create.reset();
            update.mutate({
              id: row.id,
              body: { status: row.status === "active" ? "archived" : "active" },
            });
          }}
        >
          {row.status === "active" ? t("sales.guards.archive") : t("sales.guards.restore")}
        </Button>
      ),
    },
  ];

  return (
    <section className="sales-margin-guards-screen" data-testid="sales-margin-guards-screen">
      <PageHeader
        title={t("sales.guards.title")}
        actions={
          <>
            {canManage ? (
              <Button
                variant="primary"
                data-testid="sales-margin-guard-new"
                onClick={() => {
                  create.reset();
                  update.reset();
                  setCreating(true);
                }}
              >
                {t("sales.guards.new")}
              </Button>
            ) : null}
          </>
        }
      />
      <SalesGroupTabs group="settings" />

      {canManage ? (
        <>
          {/* §7's ga-only edit cell, stated where the control would otherwise
              silently fail. */}
          <p className="sales-pending-note" data-testid="sales-guards-ga-note">
            {t("sales.guards.gaNote")}
          </p>
        </>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("sales.access.title")} body={t("sales.access.guards")} />
      ) : null}

      {!refused && guards.fetchStatus === "paused" && guards.data === undefined ? (
        <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
      ) : null}

      {!refused && canManage && guards.isPending && guards.fetchStatus !== "paused" ? (
        <TableSkeleton rows={4} cols={columns.length} label={t("sales.common.loading")} />
      ) : null}

      {!refused && guards.error !== null && !(guards.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("sales.guards.error")}
          retryLabel={t("sales.common.retry")}
          requestIdLabel={t("sales.common.requestId")}
          requestId={requestIdOf(guards.error)}
          onRetry={() => {
            void guards.refetch();
          }}
        />
      ) : null}

      {!refused &&
      canManage &&
      !guards.isPending &&
      guards.fetchStatus !== "paused" &&
      guards.error === null &&
      rows.length === 0 ? (
        <EmptyState title={t("sales.guards.emptyTitle")} body={t("sales.guards.emptyBody")} />
      ) : null}

      {create.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.guards.createFailed")}
          error={create.error}
        />
      ) : null}
      {update.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.guards.updateFailed")}
          error={update.error}
        />
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          clientPager={bookClientPager(t, i18n.language)}
          data-testid="sales-margin-guards-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("sales.guards.tableCaption")}
          rowTestId="sales-margin-guard-row"
          rowProps={(row) => ({ "data-scope": row.scope, "data-status": row.status })}
        />
      ) : null}

      {creating ? (
        <MarginGuardModal
          busy={create.isPending}
          onClose={() => {
            setCreating(false);
          }}
          onSubmit={(body) => {
            update.reset();
            create.mutate(body, {
              onSuccess: () => {
                setCreating(false);
              },
            });
          }}
        />
      ) : null}
    </section>
  );
}

function MarginGuardModal({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: MarginGuardCreateRequest) => void;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<OfferedScope>("default");
  const [skuText, setSkuText] = useState("");
  const [minMargin, setMinMargin] = useState("");

  const search = useProductSearch(skuText.trim());
  // Exact-SKU match only (the remnants `itemMatch` idiom): a substring that
  // silently guarded a DIFFERENT product would be a false answer to "which
  // product did I just protect?".
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);

  const valid = PCT.test(minMargin.trim()) && (scope === "default" || match !== undefined);

  return (
    <Modal
      heading={t("sales.guards.createTitle")}
      label={t("sales.guards.createTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-margin-guard-modal"
    >
      <div className="sales-form">
        <Field label={t("sales.guards.scopeLabel")}>
          <Select
            value={scope}
            data-testid="sales-margin-guard-scope"
            onChange={(e) => {
              setScope(e.target.value as OfferedScope);
            }}
          >
            {OFFERED_SCOPES.map((value) => (
              <option key={value} value={value}>
                {t(`sales.guards.scope.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        {scope === "product" ? (
          <Field label={t("sales.guards.product")} wide>
            <input
              value={skuText}
              placeholder={t("sales.guards.productHint")}
              data-testid="sales-margin-guard-product"
              onChange={(e) => {
                setSkuText(e.target.value);
              }}
            />
            {skuText.trim().length > 0 ? (
              match !== undefined ? (
                <span className="sales-applied" data-testid="sales-margin-guard-product-match">
                  {t("sales.guards.productApplied", { sku: match.sku, name: match.name })}
                </span>
              ) : (
                <span className="sales-applied" data-testid="sales-margin-guard-product-pending">
                  {t("sales.guards.productNoMatch")}
                </span>
              )
            ) : null}
          </Field>
        ) : null}
        <Field label={t("sales.guards.minMargin")}>
          <input
            inputMode="decimal"
            value={minMargin}
            data-testid="sales-margin-guard-min"
            onChange={(e) => {
              setMinMargin(e.target.value);
            }}
          />
        </Field>
        {/* No basis control: the server refuses `moving_avg` until the cost
            ledger it reads exists (spec 09 §2.11), and an option the
            server always refuses is a dead control (P3-UX, D-425). A
            guard saved with it elsewhere still shows its basis by name. */}
      </div>
      <div className="sales-modal-actions">
        <Button onClick={onClose}>{t("sales.common.cancel")}</Button>
        <Button
          variant="primary"
          data-testid="sales-margin-guard-submit"
          busy={busy}
          busyLabel={t("sales.common.saving")}
          disabled={!valid}
          onClick={() => {
            onSubmit({
              scope,
              ...(scope === "product" && match !== undefined ? { product_id: match.id } : {}),
              min_margin_pct: minMargin.trim(),
              cost_basis: "standard",
            });
          }}
        >
          {t("sales.common.save")}
        </Button>
      </div>
    </Modal>
  );
}
